import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import {
  CardContainer,
  CollapseSection,
  getFormattedDateTime,
  getFormattedFloat,
  getFormattedNumber,
  getFormattedPrice,
  LabeledValue,
  MenuButton,
  TableColumn,
  TableInner,
  TextWithBreaks,
} from "@msys/ui";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import { FieldArray, Form, Formik } from "formik";
import { isNil, keyBy } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { makeRefetchQuery } from "../../../clients/apollo-client-refetch-queries.js";
import {
  type ItemType,
  type PermissionName,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { AutocompleteField } from "../../commons/form-fields/AutocompleteField.js";
import { FormattedPriceField } from "../../commons/form-fields/FormattedPriceField.js";
import { ManualSave } from "../../commons/form-fields/ManualSave.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { ViewMode, ViewModeMenuItem } from "../../commons/ViewModeMenuItem.js";
import {
  getDummyMaterialNumbers,
  useSapS4HanaIntegrationSettings,
} from "./hooks.js";
import {
  S4HanaCancelReasonSelectProcess,
  S4HanaCancelReasonSelectProcessRef,
} from "./S4HanaCancelReasonSelectProcess.js";
import {
  ItemSapS4HanaDataFragment,
  ItemSapS4HanaPricingDataFragment,
  S4HanaQuoteItemDataBoxDocument,
  S4HanaQuoteItemDataBoxQueryVariables,
  useS4HanaQuoteItemDataBoxQuery,
  useSapS4HanaCancelItemMutation,
  useSapS4HanaModifyItemDataMutation,
  useSapS4HanaUncancelItemMutation,
} from "./S4HanaQuoteItemDataBox.generated.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";

type DistributiveOmit<T, K extends keyof any> = T extends any
  ? Omit<T, K>
  : never;

interface FormValues {
  itemTexts: {
    xLongTextId: string;
    xLongTextValue: string;
  }[];
  xPlant: string | null;
  xMaterialGroup: string | null;
  xMaterialByCustomer: string | null;
  xMaterialDummyConditionAmount: number | null;
  itemCategory: string | null;
  customFields: DistributiveOmit<
    ItemSapS4HanaDataFragment["customFields"],
    "translationKey"
  >;
}

interface Props {
  projectId: string;
  quoteId: string;
  itemId: string;
  itemType: ItemType;
  docViewerPermissions: PermissionName[];
  expandedItemIds: string[] | undefined;
  canEdit: boolean;
}

export const S4HanaQuoteItemDataBox = ({
  projectId,
  quoteId,
  itemId,
  itemType,
  docViewerPermissions,
  expandedItemIds,
  canEdit,
}: Props) => {
  const { t } = useTranslate(["SapS4Hana", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const cancelReasonSelectProcessRef =
    React.useRef<S4HanaCancelReasonSelectProcessRef>(null);

  const [viewMode, setViewMode] = React.useState<ViewMode>(null);

  const client = useApolloClient();

  const query = useS4HanaQuoteItemDataBoxQuery({
    client,
    variables: {
      projectId,
      quoteId,
      itemId,
    },
  });

  const sapDocData = getDataOrNull(query.data?.quoteSapS4HanaData);

  const sapItemData = getDataOrNull(
    query.data?.quoteItemSapS4HanaData
  )?.items?.at(0);

  const refetchQueries = React.useMemo(
    () => [
      makeRefetchQuery<S4HanaQuoteItemDataBoxQueryVariables>({
        document: S4HanaQuoteItemDataBoxDocument,
        variables: { projectId, quoteId, itemId },
      }),
    ],
    [projectId, quoteId, itemId]
  );

  const [cancelItem, { loading: cancelItemIsLoading }] =
    useSapS4HanaCancelItemMutation({
      client,
      variables: {
        input: { projectId, docId: quoteId, itemId, expandedItemIds },
      },
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [uncancelItem, { loading: uncancelItemIsLoading }] =
    useSapS4HanaUncancelItemMutation({
      client,
      variables: {
        input: { projectId, docId: quoteId, itemId, expandedItemIds },
      },
      refetchQueries,
      awaitRefetchQueries: true,
    });

  const availableCancelReasons =
    sapDocData?.xAvailableSalesQuotationItemSalesDocumentRejectionReasons ?? [];
  const defaultCancelReason =
    sapDocData?.xSalesQuotationItemSalesDocumentRejectionReasonDefault;
  const availableTexts = sapDocData?.xAvailableSalesQuotationItemTexts ?? [];

  return (
    <CardContainer
      isExpandable
      title={t("SAP details", { ns: "SapS4Hana" })}
      ActionButton={
        <RestrictedByDocumentPermissionWithDebug
          permission="quote_document_update"
          document={{ viewerPermissions: docViewerPermissions }}
        >
          {
            <MenuButton>
              {canEdit && (
                <ViewModeMenuItem
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  allowedModes={[null, "edit"]}
                />
              )}
              {sapItemData &&
                !!sapItemData.lastSyncedFromSapAt &&
                (!sapItemData.xSalesDocumentRejectionReason ? (
                  <MenuItem
                    onClick={async () => {
                      const cancelReasonCode =
                        await cancelReasonSelectProcessRef.current?.startProcess(
                          {
                            availableReasons: availableCancelReasons ?? [],
                            defaultReason: defaultCancelReason || null,
                            isReasonRequired: true,
                          }
                        );
                      if (cancelReasonCode === undefined) return;
                      try {
                        await cancelItem({
                          variables: {
                            input: {
                              projectId,
                              docId: quoteId,
                              itemId,
                              reason: cancelReasonCode,
                              expandedItemIds,
                            },
                          },
                        });
                        enqueueSnackbar(
                          t("Item cancelled", { ns: "SapS4Hana" })
                        );
                      } catch (e) {
                        if (e instanceof Error)
                          enqueueSnackbar(e.message, { variant: "error" });
                      }
                    }}
                    disabled={cancelItemIsLoading}
                  >
                    {t("Cancel SAP item", { ns: "SapS4Hana" })}
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={async () => {
                      try {
                        await uncancelItem({
                          variables: {
                            input: {
                              projectId,
                              docId: quoteId,
                              itemId,
                              expandedItemIds,
                            },
                          },
                        });
                        enqueueSnackbar(
                          t("Item restored", { ns: "SapS4Hana" })
                        );
                      } catch (e) {
                        if (e instanceof Error)
                          enqueueSnackbar(e.message, { variant: "error" });
                      }
                    }}
                    disabled={uncancelItemIsLoading}
                  >
                    {t("Restore SAP item", { ns: "SapS4Hana" })}
                  </MenuItem>
                ))}
            </MenuButton>
          }
        </RestrictedByDocumentPermissionWithDebug>
      }
    >
      {viewMode === null && (
        <S4HanaQuoteItemData
          sapItemData={sapItemData}
          availableCancelReasons={availableCancelReasons}
          availableItemConditionTypes={
            sapDocData?.availableItemConditionTypes ?? []
          }
          availableTexts={availableTexts}
          itemType={itemType}
        />
      )}
      {viewMode === "edit" && (
        <S4HanaQuoteItemForm
          docId={quoteId}
          projectId={projectId}
          itemId={itemId}
          sapItemData={sapItemData}
          availableItemConditionTypes={
            sapDocData?.availableItemConditionTypes ?? []
          }
          availableCancelReasons={availableCancelReasons}
          availableTexts={availableTexts}
          setViewMode={setViewMode}
          itemType={itemType}
        />
      )}
      <S4HanaCancelReasonSelectProcess ref={cancelReasonSelectProcessRef} />
    </CardContainer>
  );
};

function S4HanaQuoteItemData({
  sapItemData,
  availableCancelReasons,
  availableItemConditionTypes,
  availableTexts,
  itemType,
}: {
  sapItemData: ItemSapS4HanaDataFragment | undefined | null;
  availableCancelReasons: { label: string; value: string }[];
  availableItemConditionTypes: { label: string; value: string }[];
  availableTexts: { label: string; value: string }[];
  itemType: ItemType;
}) {
  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["SapS4Hana", "Global"]);

  const settings = useSapS4HanaIntegrationSettings();

  const sapS4HanaDataFieldMetadataByKey = React.useMemo(() => {
    return keyBy(sapItemData?.sapS4HanaDataFieldMetadata ?? [], d => d.key);
  }, [sapItemData?.sapS4HanaDataFieldMetadata]);

  const xPlantLabel = React.useMemo(() => {
    return sapS4HanaDataFieldMetadataByKey["xPlant"]?.allowedValues?.find(
      v => v.value === sapItemData?.xPlant
    )?.label;
  }, [sapS4HanaDataFieldMetadataByKey, sapItemData?.xPlant]);

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        p: 1,
      }}
    >
      <Box>
        <Grid container columns={2} spacing={1}>
          <Grid size={1}>
            <LabeledValue label={t("Reference no.", { ns: "SapS4Hana" })}>
              {sapItemData?.xId || "-"}
            </LabeledValue>
          </Grid>
          <Grid size={1}>
            <LabeledValue label={t("Item category", { ns: "SapS4Hana" })}>
              {sapItemData?.itemCategory || "-"}
            </LabeledValue>
          </Grid>
          <Grid size={1}>
            <LabeledValue
              label={t("Reference document ID", { ns: "SapS4Hana" })}
            >
              {sapItemData?.xReferenceDocumentId || "-"}
            </LabeledValue>
          </Grid>
          <Grid size={1}>
            <LabeledValue label={t("Reference item ID", { ns: "SapS4Hana" })}>
              {sapItemData?.xReferenceItemId || "-"}
            </LabeledValue>
          </Grid>
          <Grid size={1}>
            <LabeledValue label={t("Plant", { ns: "SapS4Hana" })}>
              {sapItemData?.xPlant ? (xPlantLabel ?? sapItemData.xPlant) : "-"}
            </LabeledValue>
          </Grid>
          <Grid size={1}>
            <LabeledValue label={t("Material group", { ns: "SapS4Hana" })}>
              {sapItemData?.xMaterialGroup || "-"}
            </LabeledValue>
          </Grid>
          <Grid size={1}>
            <LabeledValue
              label={t("Material by customer", { ns: "SapS4Hana" })}
            >
              {sapItemData?.xMaterialByCustomer || "-"}
            </LabeledValue>
          </Grid>
          {sapItemData?.xMaterialByCustomer &&
          getDummyMaterialNumbers(settings).includes(
            sapItemData?.xMaterialByCustomer
          ) &&
          itemType === "paid" &&
          !sapItemData?.lastSyncedFromSapAt ? (
            <Grid size={1}>
              <LabeledValue label={t("Material price", { ns: "SapS4Hana" })}>
                {!isNil(sapItemData?.xMaterialDummyConditionAmount)
                  ? getFormattedPrice(
                      sapItemData.xMaterialDummyConditionAmount,
                      language
                    )
                  : "-"}
              </LabeledValue>
            </Grid>
          ) : null}
          {sapItemData?.xSalesDocumentRejectionReason && (
            <Grid size={2}>
              <LabeledValue
                label={t("Sales rejection reason", {
                  ns: "SapS4Hana",
                })}
              >
                {availableCancelReasons?.find(
                  r => r.value === sapItemData.xSalesDocumentRejectionReason
                )?.label ?? sapItemData.xSalesDocumentRejectionReason}
              </LabeledValue>
            </Grid>
          )}
          {sapItemData?.customFields.map(field => (
            <Grid size={1}>
              <LabeledValue
                label={t(field.translationKey as any, {
                  ns: "SapS4Hana",
                })}
              >
                {field.__typename == "ItemSapS4HanaCustomFieldBoolean"
                  ? (field.valueBool?.toString() ?? "-")
                  : field.__typename === "ItemSapS4HanaCustomFieldText"
                    ? (field.valueText ?? "-")
                    : field.__typename === "ItemSapS4HanaCustomFieldDate"
                      ? (field.valueDate?.toString() ?? "-")
                      : assertNever(field)}
              </LabeledValue>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Grid container columns={2} spacing={1}>
          <Grid size={1}>
            <LabeledValue label={t("Updated", { ns: "SapS4Hana" })}>
              {sapItemData?.updatedAt
                ? getFormattedDateTime(sapItemData.updatedAt, language)
                : "-"}
            </LabeledValue>
          </Grid>
          <Grid size={1}>
            <LabeledValue label={t("Last synced", { ns: "SapS4Hana" })}>
              {sapItemData?.lastSyncedFromSapAt
                ? getFormattedDateTime(
                    sapItemData.lastSyncedFromSapAt,
                    language
                  )
                : "-"}
            </LabeledValue>
          </Grid>
        </Grid>
      </Box>
      {sapItemData && sapItemData.pricingData.length > 0 && (
        <Box sx={{ overflow: "auto" }}>
          <S4HanaQuoteItemPriceTable
            pricingData={sapItemData.pricingData}
            availableItemConditionTypes={availableItemConditionTypes}
          />
        </Box>
      )}
      <Box sx={{ overflow: "auto" }}>
        <CollapseSection
          title={t("Item texts", { ns: "SapS4Hana" })}
          isInitiallyExpanded
        >
          {sapItemData && sapItemData.itemTexts.length > 0 ? (
            <TableInner<{ id: string; value: string }>
              size="extra-small"
              columns={[
                {
                  id: "type",
                  title: t("Type", { ns: "Global" }),
                  cellStyle: { minWidth: "100px" },
                  width: "35%",
                  render: row => {
                    const type = availableTexts.find(v => v.value === row.id);
                    if (!type) return row.id;
                    return type.label;
                  },
                },
                {
                  id: "value",
                  title: t("Value", { ns: "Global" }),
                  width: "65%",
                  render: row => <TextWithBreaks text={row.value} />,
                },
              ]}
              items={sapItemData.itemTexts.map(t => ({
                id: t.xLongTextId,
                value: t.xLongTextValue,
              }))}
            />
          ) : (
            <Typography variant="body2">
              {t("Not set", { ns: "Global" })}
            </Typography>
          )}
        </CollapseSection>
      </Box>
    </Stack>
  );
}

function S4HanaQuoteItemForm({
  itemId,
  docId,
  projectId,
  sapItemData,
  availableCancelReasons,
  availableItemConditionTypes,
  availableTexts,
  setViewMode,
  itemType,
}: {
  itemId: string;
  docId: string;
  projectId: string;
  sapItemData: ItemSapS4HanaDataFragment | undefined | null;
  availableItemConditionTypes: { label: string; value: string }[];
  availableCancelReasons: { label: string; value: string }[];
  availableTexts: { label: string; value: string }[];
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  itemType: ItemType;
}) {
  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["SapS4Hana", "Global"]);
  const settings = useSapS4HanaIntegrationSettings();

  const { enqueueSnackbar } = useSnackbar();

  const initialValues: FormValues = {
    itemTexts: sapItemData?.itemTexts || [],
    xMaterialByCustomer: sapItemData?.xMaterialByCustomer || null,
    xMaterialDummyConditionAmount:
      sapItemData?.xMaterialDummyConditionAmount || null,
    xMaterialGroup: sapItemData?.xMaterialGroup || null,
    xPlant: sapItemData?.xPlant || null,
    customFields: sapItemData?.customFields || [],
    itemCategory: sapItemData?.itemCategory || null,
  };

  const sapS4HanaDataFieldMetadataByKey = React.useMemo(() => {
    return keyBy(sapItemData?.sapS4HanaDataFieldMetadata ?? [], d => d.key);
  }, [sapItemData?.sapS4HanaDataFieldMetadata]);

  const client = useApolloClient();
  const [modifyItemData, { loading }] = useSapS4HanaModifyItemDataMutation({
    client,
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      await modifyItemData({
        variables: {
          input: {
            docId,
            itemId,
            projectId,
            values: {
              itemTexts: values.itemTexts.map(t => ({
                xLongTextId: t.xLongTextId,
                xLongTextValue: t.xLongTextValue,
              })),
              xMaterialByCustomer: values.xMaterialByCustomer,
              xMaterialDummyConditionAmount:
                values.xMaterialDummyConditionAmount,
              xMaterialGroup: values.xMaterialGroup,
              xPlant: values.xPlant,
              itemCategory: values.itemCategory,
              customFields: values.customFields.map(cf =>
                cf.__typename === "ItemSapS4HanaCustomFieldBoolean"
                  ? { bool: { key: cf.key, valueBool: cf.valueBool } }
                  : cf.__typename === "ItemSapS4HanaCustomFieldDate"
                    ? { date: { key: cf.key, valueDate: cf.valueDate } }
                    : cf.__typename === "ItemSapS4HanaCustomFieldText"
                      ? { text: { key: cf.key, valueText: cf.valueText } }
                      : assertNever(cf)
              ),
            },
          },
        },
        refetchQueries: [
          makeRefetchQuery<S4HanaQuoteItemDataBoxQueryVariables>({
            document: S4HanaQuoteItemDataBoxDocument,
            variables: { projectId, quoteId: docId, itemId },
          }),
        ],
      });
      enqueueSnackbar(t("Data updated", { ns: "SapS4Hana" }));
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const validationSchema = Yup.object().shape({
    itemTexts: Yup.array().of(
      Yup.object().shape({
        xLongTextId: Yup.string()
          .required()
          .nullable()
          .label(t("Type", { ns: "Global" }))
          .test(
            "unique",
            t("Value must be unique", { ns: "Global" }),
            function (value: string | undefined | null) {
              // @ts-ignore
              const itemTexts = (this.from as any)[1].value.itemTexts as {
                xLongTextId: string;
              }[];
              const sameValues = itemTexts.filter(p => p.xLongTextId === value);
              return sameValues.length <= 1;
            }
          ),
        xLongTextValue: Yup.string()
          .required()
          .label(t("Value", { ns: "Global" })),
      })
    ),
    xPlant: Yup.string().nullable(),
    xMaterialByCustomer: Yup.string().nullable(),
    xMaterialDummyConditionAmount: Yup.number().nullable(),
    xMaterialGroup: Yup.string().nullable(),
    itemCategory: Yup.string().nullable(),
    customFields: Yup.array(), // FIXME: implement proper validation
  });

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {formikProps => (
        <Form>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              p: 1,
            }}
          >
            <Box>
              <Grid container columns={2} spacing={1}>
                <Grid size={1}>
                  <LabeledValue label={t("Reference no.", { ns: "SapS4Hana" })}>
                    {sapItemData?.xId || "–"}
                  </LabeledValue>
                </Grid>
                <Grid size={1}>
                  {sapS4HanaDataFieldMetadataByKey["itemCategory"]
                    ?.allowedValues &&
                  sapS4HanaDataFieldMetadataByKey["itemCategory"]?.allowedValues
                    ?.length > 0 ? (
                    <AutocompleteField
                      inputLabel={t("Item category", { ns: "SapS4Hana" })}
                      name={"itemCategory"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["itemCategory"]
                          ?.editable || formikProps.isSubmitting
                      }
                      options={sapS4HanaDataFieldMetadataByKey[
                        "itemCategory"
                      ]?.allowedValues?.map(v => v.value)}
                      getOptionLabel={option => {
                        const value = sapS4HanaDataFieldMetadataByKey[
                          "itemCategory"
                        ]?.allowedValues?.find(v => v.value === option);
                        if (!value) return option;
                        return value.label;
                      }}
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  ) : (
                    <TextField
                      label={t("Item category", { ns: "SapS4Hana" })}
                      name={"itemCategory"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["itemCategory"]
                          ?.editable || formikProps.isSubmitting
                      }
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  )}
                </Grid>
                <Grid size={1}>
                  <LabeledValue
                    label={t("Reference document ID", { ns: "SapS4Hana" })}
                  >
                    {sapItemData?.xReferenceDocumentId || "–"}
                  </LabeledValue>
                </Grid>
                <Grid size={1}>
                  <LabeledValue
                    label={t("Reference item ID", { ns: "SapS4Hana" })}
                  >
                    {sapItemData?.xReferenceItemId || "–"}
                  </LabeledValue>
                </Grid>
                <Grid size={1}>
                  {sapS4HanaDataFieldMetadataByKey["xPlant"]?.allowedValues &&
                  sapS4HanaDataFieldMetadataByKey["xPlant"]?.allowedValues
                    ?.length > 0 ? (
                    <AutocompleteField
                      inputLabel={t("Plant", { ns: "SapS4Hana" })}
                      name={"xPlant"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["xPlant"]?.editable ||
                        formikProps.isSubmitting
                      }
                      options={sapS4HanaDataFieldMetadataByKey[
                        "xPlant"
                      ]?.allowedValues?.map(v => v.value)}
                      getOptionLabel={option => {
                        const value = sapS4HanaDataFieldMetadataByKey[
                          "xPlant"
                        ]?.allowedValues?.find(v => v.value === option);
                        if (!value) return option;
                        return value.label;
                      }}
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  ) : (
                    <TextField
                      label={t("Plant", { ns: "SapS4Hana" })}
                      name={"xPlant"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["xPlant"]?.editable ||
                        formikProps.isSubmitting
                      }
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  )}
                </Grid>
                <Grid size={1}>
                  {sapS4HanaDataFieldMetadataByKey["xMaterialGroup"]
                    ?.allowedValues &&
                  sapS4HanaDataFieldMetadataByKey["xMaterialGroup"]
                    ?.allowedValues?.length > 0 ? (
                    <AutocompleteField
                      inputLabel={t("Material group", { ns: "SapS4Hana" })}
                      name={"xMaterialGroup"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["xMaterialGroup"]
                          ?.editable || formikProps.isSubmitting
                      }
                      options={sapS4HanaDataFieldMetadataByKey[
                        "xMaterialGroup"
                      ]?.allowedValues?.map(v => v.value)}
                      getOptionLabel={option => {
                        const value = sapS4HanaDataFieldMetadataByKey[
                          "xMaterialGroup"
                        ]?.allowedValues?.find(v => v.value === option);
                        if (!value) return option;
                        return value.label;
                      }}
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  ) : (
                    <TextField
                      label={t("Material group", { ns: "SapS4Hana" })}
                      name={"xMaterialGroup"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["xMaterialGroup"]
                          ?.editable || formikProps.isSubmitting
                      }
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  )}
                </Grid>
                <Grid size={1}>
                  {sapS4HanaDataFieldMetadataByKey["xMaterialByCustomer"]
                    ?.allowedValues &&
                  sapS4HanaDataFieldMetadataByKey["xMaterialByCustomer"]
                    ?.allowedValues?.length > 0 ? (
                    <AutocompleteField
                      inputLabel={t("Material by customer", {
                        ns: "SapS4Hana",
                      })}
                      name={"xMaterialByCustomer"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["xMaterialByCustomer"]
                          ?.editable || formikProps.isSubmitting
                      }
                      options={sapS4HanaDataFieldMetadataByKey[
                        "xPlant"
                      ]!.allowedValues!.map(v => v.value)}
                      getOptionLabel={option => {
                        const value = sapS4HanaDataFieldMetadataByKey[
                          "xMaterialByCustomer"
                        ]?.allowedValues?.find(v => v.value === option);
                        if (!value) return option;
                        return value.label;
                      }}
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  ) : (
                    <TextField
                      label={t("Material by customer", { ns: "SapS4Hana" })}
                      name={"xMaterialByCustomer"}
                      disabled={
                        !sapS4HanaDataFieldMetadataByKey["xMaterialByCustomer"]
                          ?.editable || formikProps.isSubmitting
                      }
                      sx={{ alignSelf: "flex-start", flexBasis: "35%" }}
                    />
                  )}
                </Grid>
                {formikProps.values.xMaterialByCustomer &&
                getDummyMaterialNumbers(settings).includes(
                  formikProps.values.xMaterialByCustomer
                ) &&
                itemType === "paid" &&
                !sapItemData?.lastSyncedFromSapAt ? (
                  <Grid size={1}>
                    <FormattedPriceField
                      name="xMaterialDummyConditionAmount"
                      label={t("Material price", { ns: "SapS4Hana" })}
                      type="price"
                      disabled={formikProps.isSubmitting}
                    />
                  </Grid>
                ) : null}
                {formikProps.values.customFields.map((field, index) => {
                  if (field.__typename === "ItemSapS4HanaCustomFieldBoolean") {
                    return (
                      <Grid size={1}>
                        <CheckboxField
                          key={field.translationKey}
                          label={t(field.translationKey as any, {
                            ns: "SapS4Hana",
                          })}
                          name={`customFields.${index}.valueBool`}
                          disabled={formikProps.isSubmitting}
                        />
                      </Grid>
                    );
                  } else if (
                    field.__typename === "ItemSapS4HanaCustomFieldText"
                  ) {
                    return (
                      <Grid size={1}>
                        <TextField
                          key={field.translationKey}
                          label={t(field.translationKey as any, {
                            ns: "SapS4Hana",
                          })}
                          name={`customFields.${index}.valueText`}
                          disabled={formikProps.isSubmitting}
                        />
                      </Grid>
                    );
                  } else {
                    return null;
                  }
                })}
                {sapItemData?.xSalesDocumentRejectionReason && (
                  <Grid size={2}>
                    <LabeledValue
                      label={t("Sales rejection reason", {
                        ns: "SapS4Hana",
                      })}
                    >
                      {availableCancelReasons?.find(
                        r =>
                          r.value === sapItemData.xSalesDocumentRejectionReason
                      )?.label ?? sapItemData.xSalesDocumentRejectionReason}
                    </LabeledValue>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Box>
              <Grid container columns={2} spacing={1}>
                <Grid size={1}>
                  <LabeledValue label={t("Updated", { ns: "SapS4Hana" })}>
                    {sapItemData?.updatedAt
                      ? getFormattedDateTime(sapItemData.updatedAt, language)
                      : "–"}
                  </LabeledValue>
                </Grid>
                <Grid size={1}>
                  <LabeledValue label={t("Last synced", { ns: "SapS4Hana" })}>
                    {sapItemData?.lastSyncedFromSapAt
                      ? getFormattedDateTime(
                          sapItemData.lastSyncedFromSapAt,
                          language
                        )
                      : "–"}
                  </LabeledValue>
                </Grid>
              </Grid>
            </Box>
            {sapItemData && sapItemData.pricingData.length > 0 && (
              <Box sx={{ overflow: "auto" }}>
                <S4HanaQuoteItemPriceTable
                  pricingData={sapItemData.pricingData}
                  availableItemConditionTypes={availableItemConditionTypes}
                />
              </Box>
            )}

            <Box sx={{ overflow: "auto" }}>
              <CollapseSection
                title={t("Item texts", { ns: "SapS4Hana" })}
                isInitiallyExpanded
              >
                <FieldArray name="itemTexts">
                  {arrayHelpers => (
                    <Stack direction="column" spacing={1}>
                      {formikProps.values.itemTexts.length > 0 &&
                        formikProps.values.itemTexts.map((itemText, idx) => (
                          <Stack
                            direction="row"
                            spacing={1}
                            key={`header-text-${idx}`}
                            sx={{
                              alignItems: "center",
                            }}
                          >
                            {availableTexts.length > 0 ? (
                              <AutocompleteField
                                inputLabel={t("Type", { ns: "Global" })}
                                name={`itemTexts.${idx}.xLongTextId`}
                                disabled={formikProps.isSubmitting}
                                options={availableTexts.map(v => v.value)}
                                getOptionLabel={option => {
                                  const type = availableTexts.find(
                                    v => v.value === option
                                  );
                                  if (!type) return option;
                                  return type.label;
                                }}
                                sx={{
                                  alignSelf: "flex-start",
                                  flexBasis: "35%",
                                }}
                              />
                            ) : (
                              <TextField
                                label={t("Type", { ns: "Global" })}
                                name={`itemTexts.${idx}.xLongTextId`}
                                disabled={formikProps.isSubmitting}
                                sx={{
                                  alignSelf: "flex-start",
                                  flexBasis: "35%",
                                }}
                              />
                            )}
                            <TextField
                              label={t("Value", { ns: "Global" })}
                              name={`itemTexts.${idx}.xLongTextValue`}
                              disabled={formikProps.isSubmitting}
                              multiline
                              sx={{
                                flex: 1,
                                alignSelf: "flex-start",
                                flexBasis: "65%",
                              }}
                            />
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => {
                                arrayHelpers.remove(idx);
                              }}
                              sx={{ flexGrow: 0, flexShrink: 0 }}
                              disabled={formikProps.isSubmitting}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        ))}
                      <Button
                        type="button"
                        color="secondary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          arrayHelpers.push({
                            xLongTextId: null,
                            xLongTextValue: "",
                          })
                        }
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {t("Add header text", { ns: "SapS4Hana" })}
                      </Button>
                    </Stack>
                  )}
                </FieldArray>
              </CollapseSection>
            </Box>
            <ManualSave
              disabled={formikProps.isSubmitting}
              onCancel={() => {
                setViewMode(null);
              }}
              onSubmitted={() => {
                setViewMode(null);
              }}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}

const S4HanaQuoteItemPriceTable = ({
  pricingData,
  availableItemConditionTypes,
}: {
  pricingData: ItemSapS4HanaPricingDataFragment[];
  availableItemConditionTypes: { label: string; value: string }[];
}) => {
  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["SapS4HanaPriceTable", "Global"]);
  const priceTableLabels = React.useMemo(
    () =>
      Object.fromEntries(
        availableItemConditionTypes.map(e => [e.value, e.label])
      ),
    [availableItemConditionTypes]
  );

  const columnConfig: TableColumn<ItemSapS4HanaPricingDataFragment>[] = [
    {
      id: "typeCode",
      title: t("Type code", { ns: "SapS4HanaPriceTable" }),
      width: 90,
      render: data => data.conditionType || "–",
    },
    {
      id: "description",
      title: t("Description", { ns: "SapS4HanaPriceTable" }),
      width: 180,
      render: data =>
        data.conditionType ? priceTableLabels[data.conditionType] || "" : "",
    },
    {
      id: "amountOrRate",
      title: t("Amount/rate", { ns: "SapS4HanaPriceTable" }),
      width: 120,
      render: data =>
        !isNil(data.conditionRateValue)
          ? data.conditionCalculationType === "A"
            ? getFormattedFloat(data.conditionRateValue, language, undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + "%"
            : getFormattedFloat(data.conditionRateValue, language, undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
          : "-",
    },
    {
      id: "amountOrRateCurrency",
      title: t("Currency", { ns: "SapS4HanaPriceTable" }),
      width: 90,
      render: data =>
        !isNil(data.conditionRateValue) && data.conditionCurrency
          ? data.conditionCurrency
          : "–",
    },
    {
      id: "per",
      title: t("Per", { ns: "SapS4HanaPriceTable" }),
      width: 50,
      render: data =>
        data.conditionQuantity
          ? getFormattedNumber(data.conditionQuantity, language)
          : 0,
    },
    {
      id: "unit",
      title: t("Unit", { ns: "SapS4HanaPriceTable" }),
      width: 50,
      render: data => data.conditionQuantityUnit || "",
    },
    {
      id: "conditionAmount",
      title: t("Condition amount", { ns: "SapS4HanaPriceTable" }),
      width: 120,
      render: data =>
        getFormattedFloat(data.conditionAmount || 0, language, undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      id: "conditionCurrency",
      title: t("Currency", { ns: "SapS4HanaPriceTable" }),
      width: 90,
      render: data => data.transactionCurrency || "–",
    },
    {
      id: "conversionFactor",
      title: t("Conversion factor", { ns: "SapS4HanaPriceTable" }),
      width: 120,
      render: data => "",
    },
  ];

  return (
    <TableInner
      size="extra-small"
      columns={columnConfig}
      items={pricingData}
      sx={{ minWidth: "900px" }}
    />
  );
};
